import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { CustomBenchmarkSuccessResponse } from '@/api/rest/resources/types/benchmark';
import { PlotReportFactElement } from '@/api/rest/resources/types/fact';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { AboveGroundCarbonStorageHistorical } from '@/components/Charts';
import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import { useChartZoom } from '@/components/Charts/components/ChartZoom';
import { GraphFact } from '@/components/Charts/types';
import { kgToTonneFormatter } from '@/components/Charts/utils';
import { useNCCardContext } from '@/components/NCCard/NCCard';
import { getBenchmarksFromFacts } from '@/pages/shared/hooks/useBenchmarkForFact';

import { NoPlotData } from '../NoPlotData';

export const AboveGroundCarbonCardGraphTile = ({
  graphData,
  benchmarkFact,
  activeCustomBenchmark,
  selectedBenchmarkId,
  onBenchmarkSelect,
}: {
  graphData: GraphFact[] | null | undefined;
  benchmarkFact: {
    high: PlotReportFactElement<GraphFact[] | null> | undefined;
    moderate: PlotReportFactElement<GraphFact[] | null> | undefined;
    low: PlotReportFactElement<GraphFact[] | null> | undefined;
  };
  activeCustomBenchmark?: CustomBenchmarkSuccessResponse | null;
  selectedBenchmarkId: string | null;
  onBenchmarkSelect: (id: string | null) => void;
}) => {
  const { t } = useTranslation();
  const { analysisType } = useNCCardContext();

  const { zoom, setZoom } = useChartZoom();

  const cagLowBenchmarkData = (benchmarkFact.low?.value ?? []).map((fact) => {
    return { ...fact, name: `${fact.name}_low` };
  });
  const cagModerateBenchmarkData = (benchmarkFact.moderate?.value ?? []).map((fact) => {
    return { ...fact, name: `${fact.name}_moderate` };
  });
  const cagHighBenchmarkData = (benchmarkFact.high?.value ?? []).map((fact) => {
    return { ...fact, name: `${fact.name}_high` };
  });

  const cagBenchmarks = getBenchmarksFromFacts({
    highBenchmarkFact: benchmarkFact.high,
    moderateBenchmarkFact: benchmarkFact.moderate,
    lowBenchmarkFact: benchmarkFact.low,
    formatter: kgToTonneFormatter,
  });

  const noChartData = !graphData?.length;

  if (noChartData) {
    return <NoPlotData />;
  }

  const chartData = [...graphData, ...cagLowBenchmarkData, ...cagModerateBenchmarkData, ...cagHighBenchmarkData];

  return (
    <Stack center data-testid='carbon-graph-tile'>
      <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-8 lg:pl-1'>
        <ChartHeading
          heading={t('shared.ncaDetail.details.carbon.labels.historicalAboveGroundGraphHeadline')}
          popover={{
            title: t('shared.ncaDetail.details.aboveGroundCarbon.explainers.HistoricalAboveGroundCarbonStorage.title'),
            body: t('shared.ncaDetail.details.aboveGroundCarbon.explainers.HistoricalAboveGroundCarbonStorage.body'),
          }}
          className='w-full px-6 sm:px-0'
          zoom={zoom}
          setZoom={setZoom}
          indicator={GraphTileType.historic_above_ground_carbon}
          selectedBenchmark={selectedBenchmarkId}
          setSelectedBenchmark={onBenchmarkSelect}
        />
        <AboveGroundCarbonStorageHistorical
          analysisType={analysisType}
          data={chartData}
          benchmarks={cagBenchmarks}
          customBenchmark={activeCustomBenchmark}
          showTooltip={true}
          className='relative'
          data-testid='above-ground-carbon-storage-historic-chart'
          zoom={zoom}
          setZoom={setZoom}
        />
      </Stack>
    </Stack>
  );
};
